import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import AuthCallback from './components/AuthCallback';
import Navbar from './components/layout/Navbar';
import HeroSection from './components/sections/HeroSection';
import StatsSection from './components/sections/StatsSection';
import GuidesSection from './components/sections/GuidesSection';
import WelcomeSection from './components/sections/WelcomeSection';
import PillarsSection from './components/sections/PillarsSection';
import WebsitesSection from './components/sections/WebsitesSection';
import CoursesSection from './components/sections/CoursesSection';
import BooksSection from './components/sections/BooksSection';
import AppsSection from './components/sections/AppsSection';
import ChannelsSection from './components/sections/ChannelsSection';
import GallerySection from './components/sections/GallerySection';
import LicenseSection from './components/sections/LicenseSection';
import DonationSection from './components/sections/DonationSection';
import Footer from './components/layout/Footer';
import AuthModal from './components/layout/AuthModal';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsers from './components/admin/AdminUsers';
import { AdminCourses, AdminBooks, AdminGuides, AdminApps, AdminWebsites, AdminChannels, AdminGallery } from './components/admin/AdminContentPages';
import AdminSessions from './components/admin/AdminSessions';
import AdminSettings from './components/admin/AdminSettings';
import AdminDataManager from './components/admin/AdminDataManager';
import RefundPolicy from './pages/RefundPolicy';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import { ChevronUp } from 'lucide-react';

const HomePage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    document.title = 'Inner Bridge Official Website';
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ background: '#0f0a1e', minHeight: '100vh' }}>
      <HeroSection />
      <GallerySection />
      <StatsSection />
      <GuidesSection />
      <WelcomeSection />
      <PillarsSection />
      <WebsitesSection />
      <CoursesSection />
      <BooksSection />
      <AppsSection />
      <ChannelsSection />
      <DonationSection />
      <LicenseSection />
      <Footer />
      <button
        className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        style={{ transition: 'opacity 0.3s, visibility 0.3s' }}
      >
        <ChevronUp size={24} />
      </button>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');

  const openAuth = (tab = 'login') => {
    setAuthTab(tab);
    setAuthOpen(true);
  };

  // SVG filter that turns near-white (cream) pixels transparent
  // and keeps darker (purple/silver) pixels opaque.
  const svgLogoFilter = (
    <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }} aria-hidden="true">
      <defs>
        <filter id="ibLogoRemoveBg" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    -1 -1 -1 2.85 0"
          />
          <feComponentTransfer>
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1" />
          </feComponentTransfer>
        </filter>
      </defs>
    </svg>
  );

  // Check URL fragment synchronously for session_id (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Admin panel — accessible only via the secret path. /admin silently 404s.
  const ADMIN_PATH = '/j93jdk-3-332d-43faa2-3';
  if (location.pathname.startsWith(ADMIN_PATH)) {
    return (
      <AdminLayout>
        <Routes>
          <Route path={`${ADMIN_PATH}`} element={<AdminDashboard />} />
          <Route path={`${ADMIN_PATH}/users`} element={<AdminUsers />} />
          <Route path={`${ADMIN_PATH}/courses`} element={<AdminCourses />} />
          <Route path={`${ADMIN_PATH}/books`} element={<AdminBooks />} />
          <Route path={`${ADMIN_PATH}/guides`} element={<AdminGuides />} />
          <Route path={`${ADMIN_PATH}/apps`} element={<AdminApps />} />
          <Route path={`${ADMIN_PATH}/websites`} element={<AdminWebsites />} />
          <Route path={`${ADMIN_PATH}/channels`} element={<AdminChannels />} />
          <Route path={`${ADMIN_PATH}/gallery`} element={<AdminGallery />} />
          <Route path={`${ADMIN_PATH}/data`} element={<AdminDataManager />} />
          <Route path={`${ADMIN_PATH}/sessions`} element={<AdminSessions />} />
          <Route path={`${ADMIN_PATH}/settings`} element={<AdminSettings />} />
        </Routes>
      </AdminLayout>
    );
  }

  // Silent 404 for /admin — attackers probing the obvious URL see nothing.
  if (location.pathname.startsWith('/admin')) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f0a1e', color: '#8885a0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 72, fontWeight: 700, color: '#e0dff0', margin: 0 }}>404</h1>
          <p style={{ fontSize: 16, marginTop: 8 }}>Page not found</p>
        </div>
      </div>
    );
  }

  // Public routes
  return (
    <>
      {svgLogoFilter}
      <Navbar onAuthOpen={openAuth} />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        initialTab={authTab}
      />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
